import React, { useState, useContext, useEffect } from "react";
import { svgKindIO, svgKindNode } from "../../../constants/globalConst";
import OptimizedTableEnhanced from "../OptimizedTableEnhanced";
import { UserContext } from "../../../context/UserProvider";
import moment from 'moment-timezone';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  makeStyles
} from "@material-ui/core";

// Estilos para el componente
const useStyles = makeStyles((theme) => ({
  detailsContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  detailsTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  detailsValue: {
    marginBottom: theme.spacing(0.5),
  },
  detailsDate: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  clickableRow: {
    cursor: 'pointer',
  }
}));

// Encabezados de la tabla con el formato simplificado (como TableEnhanced)
const headCells = [
  { id: "1Col", numeric: false, disablePadding: true, label: "Nombre" },
  { id: "2Col", numeric: false, disablePadding: false, label: "Tipo" },
  { id: "3Col", numeric: false, disablePadding: false, label: "Máximo" },
  { id: "4Col", numeric: true, disablePadding: false, label: "Mínimo" },
  { id: "5Col", numeric: true, disablePadding: false, label: "Promedio" },
];

const OptimizedTableDataChartWithDetails = ({ dataTable = [] }) => {
  const classes = useStyles();
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { userTimezone } = useContext(UserContext);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Forzar actualización cuando cambia la zona horaria
  useEffect(() => {
    // Incrementar el contador para forzar la re-renderización
    setForceUpdate(prev => prev + 1);
  }, [userTimezone]);

  // Función para formatear la fecha usando la zona horaria del usuario
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    try {
      // Usar moment-timezone para formatear la fecha según la zona horaria del usuario
      return moment(timestamp)
        .tz(userTimezone || 'America/Mexico_City')
        .format("DD/MM/YYYY, HH:mm:ss");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Error en fecha";
    }
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const dataTransform = (item) => {
    // Calcular promedio
    const calculateMean = (...numbers) =>
      parseFloat(
        numbers.reduce((acc, val) => acc + val, 0) / numbers.length
      ).toFixed(2);

    // Determinar el tipo de dispositivo
    const setImg = (uid) => {
      const arrayDeCadenas = uid.split("@");
      const canId = arrayDeCadenas[1];
      const kind = arrayDeCadenas[2];
      const typeSensor = arrayDeCadenas[3];
      const deviceType = (canId.length === 16 || canId === "0")
        ? svgKindNode(typeSensor, canId)
        : svgKindIO(kind);
      return deviceType;
    };

    const infoKind = setImg(item.uid);

    // Convertir valores digitales a ON/OFF
    const digitalToOnOff = (digital) => {
      const value = parseInt(digital);
      return value === 0 ? "OFF" : "ON";
    };

    // Encontrar el valor máximo y su índice
    let maxValue = -Infinity;
    let maxIndex = -1;

    // Encontrar el valor mínimo y su índice
    let minValue = Infinity;
    let minIndex = -1;

    // Buscar máximo y mínimo con sus índices
    if (item.points && item.points.length > 0) {
      item.points.forEach((value, index) => {
        if (value > maxValue) {
          maxValue = value;
          maxIndex = index;
        }
        if (value < minValue) {
          minValue = value;
          minIndex = index;
        }
      });
    }

    // Obtener las fechas correspondientes a los valores máximo y mínimo
    let maxDate = "N/A";
    let minDate = "N/A";

    // Si tenemos datos de series y los índices son válidos
    if (item.rawData && item.rawData.length > 0) {
      if (maxIndex >= 0 && maxIndex < item.rawData.length) {
        maxDate = formatDate(item.rawData[maxIndex][0]);
      }

      if (minIndex >= 0 && minIndex < item.rawData.length) {
        minDate = formatDate(item.rawData[minIndex][0]);
      }
    }

    // Formatear valores según el tipo (digital o analógico)
    const maxValueFormatted = maxValue !== -Infinity ? maxValue : "N/A";
    const minValueFormatted = minValue !== Infinity ? minValue : "N/A";

    const max = `${maxValueFormatted} ${infoKind.unit}`;
    const min = `${minValueFormatted} ${infoKind.unit}`;
    const mean = `${calculateMean(...item.points)} ${infoKind.unit}`;

    // Determinar si es digital o analógico
    const isDigital = item.yAxis === 0;

    // Formatear valores según el tipo
    const maxValueDisplay = isDigital ? digitalToOnOff(maxValueFormatted) : max;
    const minValueDisplay = isDigital ? digitalToOnOff(minValueFormatted) : min;
    const meanDisplay = isDigital ? "N/A" : mean;

    // Crear objeto con todos los datos para el diálogo de detalles
    const detailsData = {
      name: item.label,
      type: infoKind.name,
      deviceType: isDigital ? "Digital" : "Analógico",
      maxValue: maxValueDisplay,
      maxDate: maxDate,
      minValue: minValueDisplay,
      minDate: minDate,
      mean: meanDisplay,
      unit: infoKind.unit,
      uid: item.uid
    };

    return {
      key: item.uid,
      img: infoKind,
      first: item.label,
      second: infoKind.name,
      third: maxValueDisplay,
      fourth: minValueDisplay,
      fifth: meanDisplay,
      // Mantener todos los datos para el diálogo de detalles
      details: detailsData,
      onClick: () => handleRowClick(detailsData)
    };
  };

  // Transformar los datos para la tabla
  const transformedData = dataTable.map((item) => dataTransform(item));

  // Componente para el diálogo de detalles
  const DetailsDialog = ({ open, onClose, item }) => {
    if (!item) return null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          style={{
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            fontWeight: 'bold'
          }}
        >
          Detalles del dispositivo: {item.name}
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Paper className={classes.detailsContainer} elevation={2}>
                <Typography variant="h6" className={classes.detailsTitle} style={{ color: '#3B3C43' }}>
                  Información general
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>Nombre:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{item.name}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>Tipo:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{item.type}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>Categoría:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{item.deviceType}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>ID:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1" style={{ wordBreak: 'break-all' }}>{item.uid}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper className={classes.detailsContainer} elevation={2}>
                <Typography variant="h6" className={classes.detailsTitle} style={{ color: '#5BB033' }}>
                  Estadísticas
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Paper style={{ padding: '12px', backgroundColor: '#f9f9f9', marginBottom: '12px' }}>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#3B3C43' }}>
                        Valor máximo: {item.maxValue}
                      </Typography>
                      <Typography variant="body2" className={classes.detailsDate}>
                        Registrado el: {item.maxDate}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper style={{ padding: '12px', backgroundColor: '#f9f9f9', marginBottom: '12px' }}>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#3B3C43' }}>
                        Valor mínimo: {item.minValue}
                      </Typography>
                      <Typography variant="body2" className={classes.detailsDate}>
                        Registrado el: {item.minDate}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper style={{ padding: '12px', backgroundColor: '#f9f9f9' }}>
                      <Typography variant="body1" style={{ fontWeight: 'bold', color: '#3B3C43' }}>
                        Promedio: {item.mean}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #e0e0e0', padding: '16px' }}>
          <Button onClick={onClose} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Modificar las filas para hacerlas clickeables
  const rowsWithClickHandler = transformedData.map(row => ({
    ...row,
    className: classes.clickableRow,
    onRowClick: row.onClick
  }));

  return (
    <>
      <OptimizedTableEnhanced
        key={`table-data-chart-${forceUpdate}`}
        rows={rowsWithClickHandler}
        headCells={headCells}
        tittle="Información detallada (Haz clic en una fila para ver fechas y más detalles)"
        enableLink={false}
      />
      <DetailsDialog
        key={`details-dialog-${forceUpdate}`}
        open={dialogOpen}
        onClose={handleCloseDialog}
        item={selectedItem}
      />
    </>
  );
};

export default OptimizedTableDataChartWithDetails;
